import { BinaryUtils } from "./BinaryUtils";

const GZIP_GENERIC_HEADER: Uint8Array = new Uint8Array([
    0x1f, // Magic Number 0x1f -- 31
    0x8b, // Magic Number 0x8b -- 139
    8, // compression method, 8 for deflate
    0, // file flags
    0, 0, 0, 0, // 32bit timestamp
    4, // compression flags -- 4 is fastest
    10, // operating system id
]);

const CYNCLY_MAGIC: string = "BTCA";
// Cyncly header
// | Position | Value   | Description                       |
// |----------|---------|-----------------------------------|
// | 0        | 0x42    | Magic number. B in ASCII          |
// | 1        | 0x54    | Magic number. T in ASCII          |
// | 2        | 0x43    | Magic number. C in ASCII          |
// | 3        | 0x41    | Magic number. A in ASCII          |
// | 4        | 1       | Encoding Algorithm (1 for cyncly) |
// | 5        | 1       | Algorithm Version                 |
// | 6 - 7    | 0       | range of XORed bytes              | 
// | 8 - 9    | 0       | seed bytes                        |

export default class ResponseDecoder {
    //=========================================================================
    static async decode(buf: ArrayBuffer): Promise<string> {
        let decoded: string;
        let buffer: Uint8Array = new Uint8Array(buf.byteLength);
        buffer.set(new Uint8Array(buf));// copy buffer not to alter the source

        if (_isCynclyCompressed(buffer)) {
            _unseedBuffer(buffer);
            let uncompressedBuffer: Uint8Array = await _gunzipBuffer(buffer);

            decoded = BinaryUtils.convertUint8ArrayToString(uncompressedBuffer);
        } else {
            // try to convert to string directly
            decoded = BinaryUtils.convertUint8ArrayToString(buffer);
        }

        return decoded;
    }
}

//=============================================================================
function _isCynclyCompressed(buffer: Uint8Array): boolean {
    let isValid: boolean = false,
        offset: number = 0,
        magic: Uint8Array = buffer.slice(0, 4);

    offset += 4;
    let isCynclyHeader: boolean = Array
        .from(magic)
        .map((char: number) => String.fromCharCode(char))
        .join("") === CYNCLY_MAGIC;

    if (isCynclyHeader) {
        let encodingAlgo: number = buffer[offset]; // at this point offset is 4 (Encoding Algorithm)
        offset++;
        if (encodingAlgo === 1) {
            let algoVersion: number = buffer[offset]; // at this point offset is 5 (Algorithm Version)
            offset++;
            isValid = (algoVersion === 1);
        }
    }

    return isValid;
}

//=============================================================================
function _unseedBuffer(buffer: Uint8Array): void {
    let byteAddrToXOR: Uint8Array = buffer.slice(6, 8), // bytes 6-8 (inner address range of XORed bytes)
        seedBytes: Uint8Array = buffer.slice(8, 10), // bytes 8-10 (seed bytes)
        startIdx: number = byteAddrToXOR[0],
        endIdx: number = byteAddrToXOR[1];

    // XOR bytes from range
    for (let i: number = startIdx; i <= endIdx; i++) {
        buffer[i] = buffer[i] ^ seedBytes[i % seedBytes.length];
    }
}

//=============================================================================
async function _gunzipBuffer(buffer: Uint8Array): Promise<Uint8Array> {
    // inject GZIP Header
    GZIP_GENERIC_HEADER.forEach((headerByte: number, idx: number) => buffer[idx] = headerByte);

    // from this point on, buffer should be valid gzip
    let gzipBlob: Blob = new Blob([buffer.slice(0)], { type: "application/gzip" });
    let gunzippedBuffer: Uint8Array = new Uint8Array();
    let useNativeFallback: boolean = true;

    // in order to backsupport older node builds, we'll keep the require module usage for NODE builds of content engine workers
    // since the Main CiCAPI main process is built for web, even in node environment, we can't access the node:zlib module
    // @ts-ignore
    if (global && global.NODE_ENVIRONMENT) {
        try {
            // @ts-ignore
            const { unzip } = global.nodeRequire("node:zlib");

            useNativeFallback = false;

            await new Promise((resolve, reject) => {
                unzip(buffer, (err: any, buffer: Buffer) => {
                    if (err) {
                        console.error("Error unzipping buffer:", err);
                        reject(err);
                        return;
                    }
                    gunzippedBuffer = new Uint8Array(buffer);
                    resolve(null);
                });
            });
        } catch (e: any) {
            useNativeFallback = true;
            console.warn("Error using node:zlib to unzip buffer:", e.message, " Trying native fallback.");
        }
    }

    if (useNativeFallback && globalThis.DecompressionStream) {
        const decompressStream = new DecompressionStream("gzip");
        let decompressedStream: ReadableStream<Uint8Array> = gzipBlob
            .stream()
            .pipeThrough(decompressStream);

        let reader: ReadableStreamDefaultReader<Uint8Array> = decompressedStream.getReader();
        let chunk: Promise<ReadableStreamReadResult<Uint8Array>>;

        while ((chunk = reader.read())) {
            let { done, value }: ReadableStreamReadResult<Uint8Array> = await chunk;

            if (done) break;

            if (value) {
                gunzippedBuffer = BinaryUtils.concatUint8Arrays(gunzippedBuffer, value);
            }
        }
    } else if (useNativeFallback) {
        throw new Error("Unable to decompress response, not supported in this environment. ");
    }

    return gunzippedBuffer;
}